import type { UserPermissions } from '../types';

export class PermissionEngine {
  constructor(
    private userPermissions: UserPermissions = {},
    private ready = false,
  ) {}

  setPermissions(permissions: UserPermissions): void {
    this.userPermissions = { ...permissions };
  }

  isReady(): boolean {
    return this.ready;
  }

  hasPermission(resource: string, action: string): boolean {
    const actions = this.userPermissions[resource];
    if (!actions || !Array.isArray(actions)) return false;
    return actions.includes(action);
  }

  hasAnyPermission(resource: string, actions: string[]): boolean {
    if (!actions || !Array.isArray(actions) || actions.length === 0) return false;
    return actions.some((action) => this.hasPermission(resource, action));
  }

  hasAllPermissions(resource: string, actions: string[]): boolean {
    if (!actions || !Array.isArray(actions) || actions.length === 0) return false;
    return actions.every((action) => this.hasPermission(resource, action));
  }

  hasPageAccess(page: string): boolean {
    const { pages } = this.userPermissions;
    if (!Array.isArray(pages)) return false;
    if (pages.includes('*')) return true;
    return pages.includes(page);
  }

  getAllowedActions(resource: string): string[] {
    const actions = this.userPermissions[resource];
    return Array.isArray(actions) ? [...actions] : [];
  }

  getAccessibleResources(): string[] {
    return Object.keys(this.userPermissions).filter((key) => key !== 'pages'
      && key !== 'ignore_visibility'
      && Array.isArray(this.userPermissions[key])
      && this.userPermissions[key].length > 0);
  }

  getPages(): string[] {
    const { pages } = this.userPermissions;
    return Array.isArray(pages) ? pages : [];
  }

  canAccess(resource: string, action?: string): boolean {
    if (!action) {
      return this.getAllowedActions(resource).length > 0;
    }
    return this.hasPermission(resource, action);
  }
}
